import {Injectable} from '@angular/core';
import {PaginationModel, RequestParamModel} from '@coremodels';
import {BaseService} from '@coreservices';
import {Observable, Subject, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StockAllocationService {

  private draggedProduct = new Subject();
  draggedProduct$ = this.draggedProduct.asObservable();

  constructor(private baseService: BaseService) {
  }

  setDraggedProduct(id: any) {
    this.draggedProduct.next(id)
  }

  /* ############ Assembled Products — per-component allocation (PDF section 4.1) ############
   *
   * An order line holds ONE source. That says everything for an ordinary product, but an assembled
   * line is demand for several different products, each with its own stock and shipments — so when
   * they come from different places the single slot cannot record it, and the components that
   * aren't on the chosen source are left unheld. These endpoints record one decision per component.
   *
   * Served by the PHP v3 API. `component-allocation` shares no prefix with any NODE_JS_API_PATHS
   * entry, so it routes there without needing an EXCLUDED_PATHS exception.
   */

  /** Each component of one assembled order line: what it needs, what is held, and every source. */
  getComponentAllocationRows(quoteProductId: number): Observable<any> {
    return this.baseService.get(`component-allocation/rows/${quoteProductId}`, null).pipe(
      map((response: any) => response.body.data),
      catchError(err => throwError(() => err))
    );
  }



  /**
   * The source cards for ONE component of one assembled order line — Unassigned Stock, Stock On
   * Hand and each container, with the orders sitting on each. Same shape the ordinary allocation
   * cards consume, so `app-stock-allocation-content` renders it unchanged.
   */
  getComponentBoard(quoteProductId: number, componentProductId: number): Observable<any> {
    return this.baseService
      .get(`component-allocation/board/${quoteProductId}?component_product_id=${componentProductId}`, null)
      .pipe(
        map((response: any) => response.body.data),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Drags part or all of one component from one card to another. Adds to the destination and
   * subtracts from the origin in a single transaction, so a component can be split across sources
   * without either half being lost. `quantity` omitted moves everything on the origin card.
   */
  moveComponentSlice(quoteProductId: number, payload: any): Observable<any> {
    return this.baseService.post(`component-allocation/move/${quoteProductId}`, payload).pipe(
      map((response: any) => response?.data ?? response),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Trims a line's component allocations back to what it now needs, releasing the excess to
   * Unassigned. Called straight after an order-line split: the split itself is performed by the
   * Node API, which does not know component allocations exist, so the parent would otherwise keep
   * every slice it held while its quantity has dropped.
   */
  rebalanceComponentAllocations(quoteProductId: number, preferredCardKey?: string): Observable<any> {
    // `prefer` ('WAREHOUSE' or 'CONTAINER:<id>'): the card the excess is released from first.
    const body = preferredCardKey ? {prefer: preferredCardKey} : {};
    return this.baseService.post(`component-allocation/rebalance/${quoteProductId}`, body).pipe(
      map((response: any) => response?.data ?? response),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Follows a quantity just saved on the order page. A decrease is trimmed, as rebalance does. An
   * increase on a fully allocated line is topped up from the line's own sources, or, when they
   * cannot cover it, the whole line is released to Unassigned and Stock Allocation Required is
   * raised. `action` in the response says which: trimmed | covered | topped_up | released |
   * unchanged | blocked.
   */
  applyComponentQuantityChange(quoteProductId: number): Observable<any> {
    return this.baseService.post(`component-allocation/apply-quantity/${quoteProductId}`, {}).pipe(
      map((response: any) => response?.data ?? response),
      catchError(err => throwError(() => err))
    );
  }

  /** How many WHOLE units of this line stock on hand covers now, and which component limits it. */
  getBuildableNow(quoteProductId: number): Observable<any> {
    return this.baseService.get(`component-allocation/buildable/${quoteProductId}`, null).pipe(
      map((response: any) => response.body.data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Allocates every component of this line from stock on hand, all or nothing. Refused unless the
   * shelf covers the line in full, so a part-built assembled unit can never be allocated.
   */
  allocateReadyFromStock(quoteProductId: number): Observable<any> {
    return this.baseService.post(`component-allocation/allocate-ready/${quoteProductId}`, {}).pipe(
      map((response: any) => response?.data ?? response),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * The allocation cards for an assembled line, counted in ASSEMBLED UNITS: Unassigned Stock,
   * Stock On Hand and every container carrying a component, each with how many whole units it can
   * complete and the per-component detail for its tooltip.
   */
  getAssembledUnitBoard(quoteProductId: number): Observable<any> {
    return this.baseService.get(`component-allocation/assembled-board/${quoteProductId}`, null).pipe(
      map((response: any) => response.body.data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * The same cards for an assembled PRODUCT with no order line in view (By Product, Summary). The
   * figures are the sources', so they match the cards on any of its orders; no row is "this order".
   */
  getAssembledProductBoard(productId: number): Observable<any> {
    return this.baseService.get(`component-allocation/product-board/${productId}`, null).pipe(
      map((response: any) => response.body.data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Moves N assembled units between two cards. The destination supplies what it carries and the
   * rest comes from the earliest source that does, so all components of those units move together.
   */
  moveAssembledUnits(quoteProductId: number, payload: any): Observable<any> {
    return this.baseService.post(`component-allocation/move-units/${quoteProductId}`, payload).pipe(
      map((response: any) => response?.data ?? response),
      catchError(err => throwError(() => err))
    );
  }

  /** Moves ONE component of one order line to a different card (drag and drop). */
  allocateComponent(quoteProductId: number, payload: any): Observable<any> {
    return this.baseService.post(`component-allocation/allocate/${quoteProductId}`, payload).pipe(
      map((response: any) => response?.data ?? response),
      catchError(err => throwError(() => err))
    );
  }

  /* ---- §4.2 per-SKU ledger ---- */

  /** What is promised for one SKU right now, in allocation order, plus each source's capacity. */
  getSkuLedger(componentProductId: number): Observable<any> {
    return this.baseService.get(`component-allocation/ledger/${componentProductId}`, null).pipe(
      map((response: any) => response.body.data),
      catchError(err => throwError(() => err))
    );
  }

  /** What a first-come, first-served allocation would give. Read-only — nothing is written. */
  suggestSkuLedger(componentProductId: number): Observable<any> {
    return this.baseService.get(`component-allocation/suggest-ledger/${componentProductId}`, null).pipe(
      map((response: any) => response.body.data),
      catchError(err => throwError(() => err))
    );
  }

  /** Confirms the suggestion, writing across every order in the sequence. */
  applySkuLedger(componentProductId: number): Observable<any> {
    return this.baseService.post(`component-allocation/apply-ledger/${componentProductId}`, {}).pipe(
      map((response: any) => response?.data ?? response),
      catchError(err => throwError(() => err))
    );
  }

  /** Replaces this line's allocation with the rows the admin confirmed. */
  saveComponentAllocation(quoteProductId: number, rows: any[]): Observable<any> {
    return this.baseService.post(`component-allocation/save/${quoteProductId}`, { rows }).pipe(
      map((response: any) => response?.data ?? response),
      catchError(err => throwError(() => err))
    );
  }

  /** Removes this line's allocation, returning it to the automatic behaviour. */
  clearComponentAllocation(quoteProductId: number): Observable<any> {
    return this.baseService.post(`component-allocation/clear/${quoteProductId}`, {}).pipe(
      map((response: any) => response?.data ?? response),
      catchError(err => throwError(() => err))
    );
  }

  getSuppliers(requestParamModel) {
    return this.baseService.get('supplier', requestParamModel).pipe(map((response: any) => {
        return response.body.data;
      }),
      catchError(err => throwError(() => err))
    );
  }

  saveContainer(payload: any): Observable<any> {
    if (payload.id) {
      let id = payload.id;
      delete payload.id;
      return this.baseService.patch(`container/${id}`, payload)
        .pipe(map((response: any) => {
            return response.data;
          }),
          catchError(err => throwError(() => err))
        );
    } else {
      delete payload.id;
      return this.baseService.post('container', payload)
        .pipe(map((response: any) => {
            return response.data;
          }),
          catchError(err => throwError(() => err))
        );
    }
  }

  saveContainerProduct(payload: any): Observable<any> {
    if (payload.id) {
      let id = payload.id;
      delete payload.id;
      return this.baseService.patch(`container-product/${id}`, payload)
        .pipe(map((response: any) => {
            return response.data;
          }),
          catchError(err => throwError(() => err))
        );
    } else {
      return this.baseService.post('container-product', payload)
        .pipe(map((response: any) => {
            return response.data;
          }),
          catchError(err => throwError(() => err))
        );
    }
  }

  syncPOWithUnleashed(poNumber: any) {
    return this.baseService.get(`container/sync-po-with-unleashed?po_number=${poNumber}`).pipe(map((response: any) => {
        return response.body.data;
      }),
      catchError(err => throwError(() => err))
    );
  }

  syncPOOrders(payload: any) {
    return this.baseService.post(`container/sync-purchase-orders`, payload, true).pipe(map((response: any) => {
        return response;
      }),
      catchError(err => throwError(() => err))
    );
  }

  syncExistingPOOrders(payload: any) {
    return this.baseService.post(`container/sync-existing-purchase-orders`, payload, true).pipe(map((response: any) => {
        return response;
      }),
      catchError(err => throwError(() => err))
    );
  }

  // Background poll: callers own the error handling (a transient failure must not toast
  // every 3s, and a 404 just means the job expired), so the global toast is skipped.
  getSyncJobStatus(jobId: string) {
    return this.baseService.get(`container/sync-job-status/${jobId}`, null, true, true).pipe(
      map((response: any) => response.body.data),
      catchError(err => throwError(() => err))
    );
  }

  deleteContainerProduct(_id: any) {
    return this.baseService.delete('container-product/' + _id);
  }

  mergeContainers(payload: { source_container_ids: number[]; destination_container_id: number | null; merged_container_name: string }): Observable<any> {
    return this.baseService.post('container/merge-containers', payload, true).pipe(
      map((response: any) => response.data),
      catchError(err => throwError(() => err))
    );
  }

  mergeContainer(payload: any): Observable<any> {
    let id = payload.id;
    delete payload.id;

    return this.baseService.patch(`container/merge/${id}`, payload)
      .pipe(map((response: any) => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getContainers(requestParamModel) {
    return this.baseService.get('container', requestParamModel).pipe(map((response: any) => {
        const list = new PaginationModel<any>();
        const headers = response.headers;
        list.setHeaders(headers);
        list.data = response.body.data as any[];
        return list;
      }),
      catchError(err => throwError(() => err))
    );
  }

  getContainer(requestParamModel, id) {
    return this.baseService.get(`container/${id}`, requestParamModel).pipe(map((response: any) => {
        return response.body.data;
      }),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * A container, shaped for the Add / Edit Container form. Every screen that opens the form
   * fetches through here, so they cannot drift apart again: the stock-allocation cards used to
   * send their own request that also expanded every allocated line's whole quote and product —
   * none of which the form reads — and left out the sample-product fields it does read. Pulling
   * every column of each linked quote also made the request fail outright whenever the database
   * was a column behind the schema.
   */
  getContainerForEdit(id: number) {
    const requestParamModel = new RequestParamModel();
    requestParamModel.expands = [
      'container_product.product.product_image', 'container_product.product.family', 'container_product.product.material', 'container_product.product.edge', 'container_product.product.finish', 'container_product.product.unit_of_measurement', 'status', 'container_product.sample_product.unit_of_measurement'
    ];
    requestParamModel.fields = [
      'container_product.id', 'container_product.quantity', 'container_product.ul_guid',
      'container_product.ul_unit_price', 'container_product.ul_discounted_price', 'container_product.ul_tax_rate',
      'container_product.product.id', 'container_product.product.type', 'container_product.product.length', 'container_product.product.width', 'container_product.product.thickness', 'container_product.product.sku',
      'container_product.product.product_image.path', 'container_product.product.product_image.type', 'container_product.product.format_icon',
      'container_product.product.family.name', 'container_product.product.material.name', 'container_product.product.edge.name', 'container_product.product.finish.name',
      'container_product.product.unit_of_measurement.name',
      'container_product.product.is_custom_product', 'container_product.product.ul_product_description', 'status.short_name', 'status.id',
      // Sample product fields
      'container_product.sample_product.id', 'container_product.sample_product.sku', 'container_product.sample_product.ul_product_description', 'container_product.sample_product.ul_stock_on_hand', 'container_product.sample_product.ul_allocated_qty', 'container_product.sample_product.ul_available_qty', 'container_product.sample_product.ul_on_purchase', 'container_product.sample_product.unit_of_measurement.name'
    ];
    delete requestParamModel.pageSize;
    return this.getContainer(requestParamModel, id);
  }

  deleteContainer(id: any) {
    return this.baseService.delete(`container/${id}`)
      .pipe(map(response => {
          return response;
        }),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Check if a container with the given PO number already exists in the database
   * @param poNumber - The Purchase Order number to check
   * @param excludeContainerId - Optional container ID to exclude from the check (for edit mode)
   * @returns Observable with the existing container data if found, or null if not found
   */
  checkContainerExists(poNumber: string, excludeContainerId?: number): Observable<any> {
    const requestParamModel = new RequestParamModel();
    requestParamModel.addDefaultFilters('purchase_order_number', poNumber, 'string');
    requestParamModel.expands = ['status'];
    requestParamModel.fields = ['id', 'purchase_order_number', 'status.name', 'status.short_name'];
    requestParamModel.pageSize = 1;

    return this.baseService.get('container', requestParamModel).pipe(
      map((response: any) => {
        const containers = response.body.data as any[];
        if (containers && containers.length > 0) {
          // If we're in edit mode, exclude the current container from the check
          if (excludeContainerId) {
            const otherContainer = containers.find(c => c.id !== excludeContainerId);
            return otherContainer || null;
          }
          return containers[0];
        }
        return null;
      }),
      catchError(err => throwError(() => err))
    );
  }

}
